import { Button, Form, Popup } from '@approvalmax/ui/src/components';
import { useCurrencyPrecision } from 'pages/requestForm/containers/netsuite/hooks';
import { FC, memo, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import {
    AddressLinesInformation,
    ContactInformation,
    CurrenciesInformation,
    FinancialInformation,
    PrimaryInformation,
    SubsidiariesInformation,
} from '..';
import { useNetSuiteVendorCreationForm } from './NetSuiteVendorCreationPopupForm.hooks';
import { messages } from './NetSuiteVendorCreationPopupForm.messages';
import { CurrenciesMap, NetSuiteVendorCreationPopupFormProps } from './NetSuiteVendorCreationPopupForm.types';

export const NetSuiteVendorCreationPopupForm: FC<NetSuiteVendorCreationPopupFormProps> = memo((props) => {
    const { terms, subsidiaries, currencies, popupTitle, ocrData, requestSubsidiary, onSave } = props;

    const currenciesMap = useMemo(() => {
        const map: CurrenciesMap = new Map();

        for (const currency of currencies) {
            map.set(currency.id, currency);
        }

        return map;
    }, [currencies]);

    const { form, handleSubmit, isLoadingCreateNetSuiteVendor, withTaxNumber } = useNetSuiteVendorCreationForm({
        currenciesMap,
        subsidiaries,
        requestSubsidiary,
        ocrData,
        onSave,
    });

    const currencyId = useWatch({ name: 'currencyId', control: form.control });
    const currency = useMemo(() => currenciesMap.get(currencyId), [currenciesMap, currencyId]);
    const currencyPrecision = useCurrencyPrecision(currency);

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <Popup.Header
                title={popupTitle}
                actions={
                    <Button
                        type='submit'
                        formNoValidate
                        title={messages.createButton}
                        progress={isLoadingCreateNetSuiteVendor}
                        color='blue80'
                        size='medium'
                    >
                        {messages.createButton}
                    </Button>
                }
            />

            <Popup.Body>
                <PrimaryInformation />

                <ContactInformation />

                <FinancialInformation
                    terms={terms}
                    withTaxNumber={withTaxNumber}
                    currencyPrecision={currencyPrecision}
                />

                <SubsidiariesInformation subsidiaries={subsidiaries} currenciesMap={currenciesMap} />

                <CurrenciesInformation currencies={currencies} />

                <AddressLinesInformation />
            </Popup.Body>
        </Form>
    );
});

NetSuiteVendorCreationPopupForm.displayName = 'NetSuiteVendorCreationPopupForm';
